import { Box, Button, Dialog, DialogContent, DialogTitle, IconButton, Skeleton, useTheme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Link } from 'react-router-dom';
import { useGetJobApplicationDetail } from '../hooks/useGetJobApplicationDetail';
import BrandCard from './BrandCard';
import ProCard from './ProCard';
import JobOfferCard from './JobOfferCard';
import TalentCard from './TalentCard';
import ResumeViewer from './ResumeViewer';
import TalentContactSection from './TalentContactSection';

interface JobApplicationDetailDialogProps {
    id: string | null;
    onClose: () => void;
}

const DetailSkeleton = () => (
    <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 2, mb: 3 }}>
            {[1, 2, 3, 4].map((i) => (
                <Skeleton key={i} variant="rounded" height={96} />
            ))}
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 3 }}>
            <Skeleton variant="rounded" height={600} />
            <Skeleton variant="rounded" height={400} />
        </Box>
    </Box>
);

const JobApplicationDetailDialog = ({ id, onClose }: JobApplicationDetailDialogProps) => {
    const { application, loading, refetch } = useGetJobApplicationDetail(id ?? undefined);
    const theme = useTheme();

    const talent = application?.talent;
    const jobOffer = application?.jobOffer;
    const pro = application?.pro;
    const brand = jobOffer?.brand;

    const resumeUrl = application?.resumeFile?.url || application?.resume || talent?.resume?.path;
    const resumeFileId = talent?.files?.find(f => f.context === 'resume')?._id ?? application?.resumeFile?._id ?? null;

    const headingText = loading
        ? '...'
        : [talent?.firstName, talent?.lastName].filter(Boolean).join(' ') +
          (jobOffer?.title ? ` — ${jobOffer.title}` : '');

    return (
        <Dialog
            open={!!id}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{ sx: { minHeight: '80vh' } }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    pr: 1,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                }}
            >
                <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', mr: 2 }}>
                    {headingText}
                </Box>
                <IconButton onClick={onClose} size="small" aria-label="Fermer">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 0 }}>
                {loading ? (
                    <DetailSkeleton />
                ) : (
                    <Box sx={{ p: 3 }}>
                        {/* Summary cards */}
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                                gap: 2,
                                mb: 3,
                            }}
                        >
                            {pro && <ProCard pro={pro} />}
                            {brand && <BrandCard brand={brand} />}
                            {jobOffer && <JobOfferCard jobOffer={jobOffer} />}
                            {talent && <TalentCard talent={talent} />}
                        </Box>
                        {/* Body */}
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' },
                                gap: 3,
                                alignItems: 'start',
                            }}
                        >
                            <ResumeViewer
                                resumeUrl={resumeUrl}
                                fileId={resumeFileId}
                                talentId={talent?._id}
                                onUploadSuccess={() => refetch()}
                            />
                            <TalentContactSection talent={talent} application={application} />
                        </Box>
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default JobApplicationDetailDialog;
